import {
  ArrowDownTrayIcon,
  PhotoIcon,
  DocumentTextIcon,
  CommandLineIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  ComputerDesktopIcon,
} from '@heroicons/react/24/outline'
import { useState, useEffect } from 'react'
import { exportAPI, projectAPI } from '../../services/api'

interface ExportPanelProps {
  projectId: string
}

type ExportMode = 'gui' | 'cli'

export default function ExportPanel({ projectId }: ExportPanelProps) {
  const [exportingImages, setExportingImages] = useState(false)
  const [exportingAnnotations, setExportingAnnotations] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [projectName, setProjectName] = useState<string>('')
  const [exportMode, setExportMode] = useState<ExportMode>('gui')
  const [copiedCommand, setCopiedCommand] = useState<string | null>(null)

  // Resolve API base URL for CLI snippets and requests
  const envUrl = (import.meta.env.VITE_API_URL || '').trim()
  const apiBaseUrl = (envUrl || window.location.origin).replace(/\/+$/, '')

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const response = await projectAPI.get(projectId)
        setProjectName(response.data.name)
      } catch (err) {
        console.error('Failed to fetch project:', err)
      }
    }
    fetchProject()
  }, [projectId])

  const handleExportImages = async () => {
    try {
      setExportingImages(true)
      setError(null)

      const response = await exportAPI.exportImages(projectId)

      // Handle file download
      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/zip',
      })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url

      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition']
      const filenameMatch = contentDisposition?.match(/filename="(.+)"/)
      const filename = filenameMatch ? filenameMatch[1] : `exported-${projectName || 'project'}.zip`

      a.download = filename
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (err: any) {
      console.error('Export images error:', err)
      setError('Failed to export images. Please try again.')
    } finally {
      setExportingImages(false)
    }
  }

  const handleExportMergedAnnotations = async () => {
    try {
      setExportingAnnotations(true)
      setError(null)

      const response = await exportAPI.exportMergedAnnotations(projectId)

      // Handle JSON download
      const blob = new Blob([JSON.stringify(response.data, null, 2)], {
        type: 'application/json',
      })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `exported-${projectName || 'project'}.json`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (err: any) {
      console.error('Export annotations error:', err)
      setError('Failed to export annotations. Please try again.')
    } finally {
      setExportingAnnotations(false)
    }
  }

  // Generate CURL commands
  const generateCurlCommand = (type: 'images' | 'annotations') => {
    const token = localStorage.getItem('token')
    const apiUrl = apiBaseUrl
    const endpoint =
      type === 'images'
        ? `/api/v1/export/projects/${projectId}/images`
        : `/api/v1/export/projects/${projectId}/merged-annotations`

    const outputFile =
      type === 'images'
        ? `${projectName || 'project'}_images.zip`
        : `${projectName || 'project'}_annotations.json`

    // Build the CURL command
    const curlCommand = `curl -X GET \\
  "${apiUrl}${endpoint}" \\
  -H "Authorization: Bearer ${token || 'YOUR_JWT_TOKEN'}" \\
  -H "Accept: ${type === 'images' ? 'application/octet-stream' : 'application/json'}" \\
  -o "${outputFile}" \\
  -L`

    return curlCommand
  }

  const copyToClipboard = async (text: string, commandId: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedCommand(commandId)
      setTimeout(() => setCopiedCommand(null), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Export Project Data</h3>
        <p className="text-sm text-gray-600">
          Download project images and merged annotation data for further processing.
        </p>
      </div>

      {/* Export Mode Selector */}
      <div className="flex items-center gap-2 bg-white rounded-lg border border-gray-200 p-1">
        <button
          onClick={() => setExportMode('gui')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            exportMode === 'gui'
              ? 'bg-blue-50 text-blue-700 shadow-sm'
              : 'text-gray-700 hover:bg-gray-50'
          }`}
        >
          <ComputerDesktopIcon className="w-5 h-5" />
          Browser Download
        </button>
        <button
          onClick={() => setExportMode('cli')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            exportMode === 'cli'
              ? 'bg-blue-50 text-blue-700 shadow-sm'
              : 'text-gray-700 hover:bg-gray-50'
          }`}
        >
          <CommandLineIcon className="w-5 h-5" />
          CURL Command
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {exportMode === 'gui' ? (
        /* GUI Mode - Original buttons */
        <div className="space-y-4">
          {/* Export Images */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <PhotoIcon className="w-6 h-6 text-blue-600" />
                  <h4 className="text-base font-medium text-gray-900">Export All Images</h4>
                </div>
                <p className="text-sm text-gray-600">
                  Download all project images as a ZIP file. This includes all uploaded images in
                  their original format.
                </p>
              </div>
              <button
                onClick={handleExportImages}
                disabled={exportingImages}
                className="ml-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ArrowDownTrayIcon className="w-5 h-5 mr-2" />
                {exportingImages ? 'Exporting...' : 'Download Images'}
              </button>
            </div>
          </div>

          {/* Export Merged Annotations */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <DocumentTextIcon className="w-6 h-6 text-green-600" />
                  <h4 className="text-base font-medium text-gray-900">Export Merged Annotations</h4>
                </div>
                <p className="text-sm text-gray-600">
                  Download merged annotations from all annotators in COCO JSON format. This file
                  contains averaged keypoint positions and bounding boxes.
                </p>
              </div>
              <button
                onClick={handleExportMergedAnnotations}
                disabled={exportingAnnotations}
                className="ml-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ArrowDownTrayIcon className="w-5 h-5 mr-2" />
                {exportingAnnotations ? 'Exporting...' : 'Download Annotations'}
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* CLI Mode - CURL commands */
        <div className="space-y-4">
          {/* Export Images CURL */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-3">
              <PhotoIcon className="w-6 h-6 text-blue-600" />
              <h4 className="text-base font-medium text-gray-900">
                Export All Images - CURL Command
              </h4>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Use this command to download all project images as a ZIP file from your terminal:
            </p>
            <div className="relative">
              <pre className="bg-gray-900 text-gray-300 p-4 rounded-lg overflow-x-auto text-sm font-mono">
                <code className="text-gray-300">{generateCurlCommand('images')}</code>
              </pre>
              <button
                onClick={() => copyToClipboard(generateCurlCommand('images'), 'images-curl')}
                className="absolute top-2 right-2 p-2 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-md transition-colors"
                title="Copy command"
              >
                {copiedCommand === 'images-curl' ? (
                  <CheckIcon className="w-5 h-5 text-green-400" />
                ) : (
                  <ClipboardDocumentIcon className="w-5 h-5" />
                )}
              </button>
            </div>
            {!localStorage.getItem('token') && (
              <p className="mt-2 text-xs text-amber-600">
                ⚠️ Note: Replace YOUR_JWT_TOKEN with your actual authentication token
              </p>
            )}
          </div>

          {/* Export Annotations CURL */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-3">
              <DocumentTextIcon className="w-6 h-6 text-green-600" />
              <h4 className="text-base font-medium text-gray-900">
                Export Merged Annotations - CURL Command
              </h4>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Use this command to download merged annotations in COCO format from your terminal:
            </p>
            <div className="relative">
              <pre className="bg-gray-900 text-gray-300 p-4 rounded-lg overflow-x-auto text-sm font-mono">
                <code className="text-gray-300">{generateCurlCommand('annotations')}</code>
              </pre>
              <button
                onClick={() =>
                  copyToClipboard(generateCurlCommand('annotations'), 'annotations-curl')
                }
                className="absolute top-2 right-2 p-2 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-md transition-colors"
                title="Copy command"
              >
                {copiedCommand === 'annotations-curl' ? (
                  <CheckIcon className="w-5 h-5 text-green-400" />
                ) : (
                  <ClipboardDocumentIcon className="w-5 h-5" />
                )}
              </button>
            </div>
            {!localStorage.getItem('token') && (
              <p className="mt-2 text-xs text-amber-600">
                ⚠️ Note: Replace YOUR_JWT_TOKEN with your actual authentication token
              </p>
            )}
          </div>

          {/* CLI Instructions */}
          <div className="bg-blue-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-blue-900 mb-2">Using CURL Commands</h4>
            <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
              <li>These commands use your current authentication token automatically</li>
              <li>
                The <code className="bg-blue-100 px-1 rounded">-L</code> flag follows redirects if
                needed
              </li>
              <li>Output files will be saved in your current directory</li>
              <li>
                You can modify the output filename with the{' '}
                <code className="bg-blue-100 px-1 rounded">-o</code> parameter
              </li>
              <li>
                Add <code className="bg-blue-100 px-1 rounded">--progress-bar</code> to see download
                progress
              </li>
            </ul>

            {/* Token retrieval for automation */}
            <div className="mt-3 pt-3 border-t border-blue-200">
              <p className="text-xs font-medium text-blue-900 mb-1">
                For automation scripts, get your token programmatically:
              </p>
              <pre className="bg-blue-100 text-blue-900 p-2 rounded text-xs font-mono overflow-x-auto">
                {`# Login and get token
TOKEN=$(curl -X POST "${apiBaseUrl}/api/v1/auth/login" \\
  -H "Content-Type: application/x-www-form-urlencoded" \\
  -d "username=YOUR_EMAIL&password=YOUR_PASSWORD" \\
  | grep -o '"access_token":"[^"]*' | cut -d'"' -f4)

# Use token in export commands
curl -H "Authorization: Bearer $TOKEN" ...`}
              </pre>
            </div>
          </div>
        </div>
      )}

      {/* Information */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-900 mb-2">Export Information</h4>
        <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
          <li>
            <strong>Images:</strong> All project images packaged in a ZIP file
          </li>
          <li>
            <strong>Merged Annotations:</strong> COCO format JSON with averaged annotations across
            all annotators
          </li>
          <li>
            The merged annotations include: categories, images metadata, and averaged
            keypoints/bounding boxes
          </li>
          <li>Only annotations from users marked as "completed" are included in the export</li>
        </ul>
      </div>
    </div>
  )
}
